import apiClient from "@apis/index";
import Routes from "@apis/routes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

// ============== Check User Exists Types ==============

export interface UserExistsResponse {
  exists: boolean;
  status: number;
  registration_status: string;
}

export interface CheckUserExistsResult {
  exists: boolean;
  username?: string;
}

// ============== Step 1: Create User Types ==============

export interface RegistrationFormData {
  // Step 1 fields
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
  username?: string;
  password: string;
  password_confirmation: string;
  is_newsletter_check?: boolean;

  // Step 2 fields (used when building combined payload)
  title?: string;
  website?: string;
  business_type?: string | string[];
  phone_number?: string;
  phone_number_country_code?: string;
  phone_number_type?: string;
  mobile_phone?: string;
  mobile_phone_country_code?: string;
  mobile_phone_type?: string;
  street_address?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  heard_about_us?: string;
  is_list_check?: boolean;
}

export interface RegistrationConfig {
  organizationId: string;
  userType?: string;
  preferredLanguage?: string;
  status?: number;
}

export interface CompanyAttributes {
  name: string;
  website?: string;
  business_type?: string;
  phone_number_type?: string;
  mobile_phone_type?: string;
}

export interface UserAttributes {
  password_confirmation: string;
  email_confirmation?: string;
  newsletter?: boolean | "accepted";
}

export interface AdditionalAttributes {
  preferred_language: string;
  is_newsletter_check?: boolean;
  is_list_check?: "accepted" | "";
  company_attributes: CompanyAttributes;
  user_attributes: UserAttributes;
  username_confirmation?: string;
}

export interface ModelValue {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  password: string;
  role: string;
  status: number;
  organization_id: string;
  company_name?: string;
  additional_attributes?: AdditionalAttributes;
}

export interface RegistrationPayload {
  model_type: string;
  model_value: ModelValue;
}

export interface RegistrationResponseBody {
  model_object: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    company_name?: string;
    [key: string]: unknown;
  };
  registration_token?: string;
}

export interface RegistrationResponse {
  response: {
    body: RegistrationResponseBody;
  };
}

// ============== Step 2: Update Registration Types ==============

export interface Step2FormData {
  heard_about_us?: string;
  phone_number_country_code?: string;
  phone_number?: string;
  mobile_phone_country_code?: string;
  mobile_phone?: string;
  street_address?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  website?: string;
  title?: string;
  business_type?: string | string[];
  phone_number_type?: string;
  mobile_phone_type?: string;
  is_list_check?: boolean;
}

export interface Step2CompanyAttributes {
  phone_number_type?: string;
  mobile_phone_type?: string;
  business_type?: string[];
}

export interface Step2AdditionalAttributes {
  company_attributes?: Step2CompanyAttributes;
  user_attributes?: {
    mandatory_kyc_flow: boolean;
  };
}

export interface Step2Payload {
  registration_step: 2;
  model_value: {
    heard_about_us?: string;
    phone_number_country_code?: string;
    phone_number?: string;
    mobile_phone_country_code?: string;
    mobile_phone?: string;
    street_address?: string;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    website?: string;
    title?: string;
    additional_attributes?: Step2AdditionalAttributes;
  };
}

// ============== Step 3: Document Submission Types ==============

export interface Step3Payload {
  registration_step: 3;
  company_proof: any[];
  identity_verification: any[];
}

// ============== Update Registration Types ==============

export type UpdateRegistrationPayload = Step2Payload | Step3Payload;

export interface UpdateRegistrationParams {
  userId: number;
  registrationToken: string;
  payload: UpdateRegistrationPayload;
}

export interface UpdateRegistrationResponse {
  success: boolean;
  message?: string;
  user?: {
    user_id: number;
    email: string;
    first_name: string;
    email_verified_status: boolean;
    company_exists: boolean;
  };
  data?: {
    document_upload_mandatory?: boolean;
    email_verified_status?: boolean;
    document_upload_status?: boolean;
    activation_status?: boolean;
    [key: string]: unknown;
  };
}

// ============== Registration Session Types ==============

export interface RegistrationSession {
  userId: number | null;
  registrationToken: string | null;
  email: string | null;
  companyName: string | null;
  location: string | null;
  currentStep: 1 | 2 | 3;
  step1Completed: boolean;
  step2Completed: boolean;
  step3Completed: boolean;
}

// ============== Location API Types ==============

export interface CountryListResponse {
  data: {
    countries: string[];
  };
}

export interface StatesListResponse {
  data: {
    states: string[];
  };
}

export interface CitiesListResponse {
  data: {
    cities: string[];
  };
}

export interface BusinessTypeOption {
  name: string;
}

export interface BusinessTypesResponse {
  data: {
    business_types: BusinessTypeOption[];
  };
}

export interface CountryFromIpResponse {
  data: {
    getCountryFromIP: CountryFromIpData[];
  };
}

export interface CountryFromIpData {
  continent: string;
  country: string;
  dialCode: string;
  iso2: string;
}

export interface SelectOption {
  id: string;
  label: string;
}
export async function checkUserExists(
  email: string
): Promise<UserExistsResponse> {
  try {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      return { exists: false, status: 400 };
    }

    const response = await apiClient.get<UserExistsResponse>(
      Routes.CHECK_USER_EXISTS,
      {
        params: {
          email: trimmedEmail,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error checking user existence:", error);
    return { exists: false, status: 500 };
  }
}

export function buildRegistrationPayload(
  formData: RegistrationFormData,
  config: RegistrationConfig
): RegistrationPayload {
  const {
    // Step 1 fields
    first_name,
    last_name,
    email,
    company_name,
    password,
    password_confirmation,
    is_newsletter_check,
  } = formData;

  const {
    organizationId,
    userType = "User",
    preferredLanguage = "en",
    status = 0,
  } = config;

  const payload: RegistrationPayload = {
    model_type: userType,
    model_value: {
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      email: email.trim().toLowerCase(),
      username: email.trim().toLowerCase(),
      password: password,
      role: "user",
      status: status,
      organization_id: organizationId,
      company_name: company_name.trim(),
      additional_attributes: {
        preferred_language: preferredLanguage,
        user_attributes: {
          password_confirmation: password_confirmation,
        },
        ...(is_newsletter_check ? { is_newsletter_check: "accepted" } : {}),
      },
    },
  };

  // // Step 2 fields - Company details
  // const modelValue = payload.model_value as any;
  // const companyAttrs = payload.model_value.additional_attributes
  //   .company_attributes as any;

  // // Job Title
  // if (title) {
  //   modelValue.title = title.trim();
  // }

  // // Website
  // if (website) {
  //   companyAttrs.website = website.trim();
  // }

  // // Business type (may be array)
  // if (business_type && business_type.length > 0) {
  //   companyAttrs.business_type = Array.isArray(business_type)
  //     ? business_type.join(", ")
  //     : business_type;
  // }

  // // Phone numbers
  // if (phone_number) {
  //   modelValue.phone_number = phone_number.replace(/[^\d]/g, "");
  //   if (phone_number_country_code) {
  //     modelValue.phone_number_country_code = phone_number_country_code;
  //   }
  // }

  // if (phone_number_type) {
  //   companyAttrs.phone_number_type = phone_number_type;
  // }

  // if (mobile_phone) {
  //   modelValue.mobile_phone = mobile_phone.replace(/[^\d]/g, "");
  //   if (mobile_phone_country_code) {
  //     modelValue.mobile_phone_country_code = mobile_phone_country_code;
  //   }
  // }

  // if (mobile_phone_type) {
  //   companyAttrs.mobile_phone_type = mobile_phone_type;
  // }

  // // Address
  // if (street_address) {
  //   modelValue.street_address = street_address.trim();
  // }

  // if (country) {
  //   modelValue.country = country;
  // }

  // if (state) {
  //   modelValue.state = state;
  // }

  // if (city) {
  //   modelValue.city = city;
  // }

  // if (zip) {
  //   modelValue.zip = zip.trim();
  // }

  // // How did you hear about us
  // if (heard_about_us) {
  //   modelValue.heard_about_us = heard_about_us.trim();
  // }

  // // List on VDB checkbox
  // if (is_list_check) {
  //   payload.model_value.additional_attributes.is_list_check = "accepted";
  // }

  return payload;
}

/**
 * Hook for Step 1: Create user registration
 * Returns the mutation with typed response for capturing userId and registrationToken
 */
export const useNativeRegistration = () => {
  return useMutation({
    mutationFn: async (
      data: RegistrationPayload
    ): Promise<AxiosResponse<RegistrationResponse>> => {
      return apiClient.post<RegistrationResponse>(
        Routes.DYNAMIC_REGISTRATION,
        data
      );
    },
  });
};

/**
 * Builds the Step 2 payload from form data
 * Transforms form values to API-expected format
 *
 * Payload structure:
 * {
 *   registration_step: 2,
 *   model_value: {
 *     heard_about_us, phone_number_country_code, phone_number,
 *     mobile_phone_country_code, mobile_phone, street_address,
 *     country, state, city, zip, website,
 *     additional_attributes: {
 *       company_attributes: {
 *         phone_number_type, mobile_phone_type, business_type[]
 *       }
 *     }
 *   }
 * }
 */
export function buildStep2Payload(
  formData: Step2FormData,
  documentUploadMandatory: boolean
): Step2Payload {
  const modelValue: Step2Payload["model_value"] = {};

  // Basic fields
  if (formData.heard_about_us) {
    modelValue.heard_about_us = formData.heard_about_us.trim();
  }

  if (formData.phone_number_country_code) {
    modelValue.phone_number_country_code = formData.phone_number_country_code;
  }

  if (formData.phone_number) {
    modelValue.phone_number = formData.phone_number.replace(/[^\d]/g, "");
  }

  if (formData.mobile_phone_country_code) {
    modelValue.mobile_phone_country_code = formData.mobile_phone_country_code;
  }

  if (formData.mobile_phone) {
    modelValue.mobile_phone = formData.mobile_phone.replace(/[^\d]/g, "");
  }

  if (formData.street_address) {
    modelValue.street_address = formData.street_address.trim();
  }

  if (formData.country) {
    modelValue.country = formData.country;
  }

  if (formData.state) {
    modelValue.state = formData.state;
  }

  if (formData.city) {
    modelValue.city = formData.city;
  }

  if (formData.zip) {
    modelValue.zip = formData.zip.trim();
  }

  if (formData.website) {
    modelValue.website = formData.website.trim();
  }

  if (formData.title) {
    modelValue.title = formData.title.trim();
  }

  // Company attributes (phone_number_type, mobile_phone_type, business_type)
  // These go inside additional_attributes.company_attributes
  const companyAttributes: Step2CompanyAttributes = {};

  if (formData.phone_number_type) {
    companyAttributes.phone_number_type = formData.phone_number_type;
  }

  if (formData.mobile_phone_type) {
    companyAttributes.mobile_phone_type = formData.mobile_phone_type;
  }

  if (formData.business_type) {
    // Ensure business_type is always an array
    companyAttributes.business_type = Array.isArray(formData.business_type)
      ? formData.business_type
      : [formData.business_type];
  }

  modelValue.additional_attributes = {
    ...(Object.keys(companyAttributes).length > 0
      ? { company_attributes: companyAttributes }
      : {}),
    user_attributes: {
      mandatory_kyc_flow: documentUploadMandatory,
    },
    ...(formData.is_list_check === true ? { is_list_check: "accepted" } : {}),
  };

  return {
    registration_step: 2,
    model_value: modelValue,
  };
}

/**
 * Builds the Step 3 payload with document URLs
 */
export function buildStep3Payload(
  companyProofUrls: any[],
  identityVerificationUrls: any[]
): Step3Payload {
  return {
    registration_step: 3,
    company_proof: companyProofUrls,
    identity_verification: identityVerificationUrls,
  };
}

/**
 * API function to update registration (Steps 2 & 3)
 * Requires X-Registration-Token header
 */
export async function updateRegistration({
  userId,
  registrationToken,
  payload,
}: UpdateRegistrationParams): Promise<UpdateRegistrationResponse> {
  try {
    const response = await apiClient.patch<UpdateRegistrationResponse>(
      Routes.UPDATE_REGISTRATION(userId),
      payload,
      {
        headers: {
          "X-Registration-Token": registrationToken,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating registration:", error);
    throw error;
  }
}

/**
 * Hook for updating registration (Steps 2 & 3)
 */
export const useUpdateRegistration = () => {
  return useMutation({
    mutationFn: updateRegistration,
  });
};

// ============== Location & Options API Functions ==============

/**
 * Fetches list of countries
 * @returns Promise with array of country SelectOptions
 */
export const useCountries = () => {
  return useQuery({
    queryKey: ["COUNTRY_LIST"],
    queryFn: async () => {
      const response = await apiClient.get<CountryListResponse>(
        Routes.COUNTRY_LIST
      );

      const countries = response?.data?.data?.countries || [];

      return countries.map((country: string) => ({
        id: country,
        label: country,
      }));
    },
  });
};

/**
 * Fetches list of states for a given country
 * @param countryName - The country name to fetch states for
 * @returns Promise with array of state SelectOptions
 */
export const useStates = (countryName: string) => {
  return useQuery({
    queryKey: ["STATES_LIST", countryName],
    enabled: !!countryName,
    queryFn: async () => {
      const response = await apiClient.get<StatesListResponse>(
        Routes.STATES_LIST,
        {
          params: {
            country_name: countryName,
          },
        }
      );

      const states = response?.data?.data?.states || [];

      return states.map((state: string) => ({
        id: state,
        label: state,
      }));
    },
  });
};

/**
 * Fetches list of cities based on country and optionally state
 * @param countryName - The country name
 * @param stateName - The state name (optional, required for US/Canada)
 * @returns Promise with array of city SelectOptions
 */
export const useCities = (countryName: string, stateName?: string) => {
  return useQuery({
    queryKey: ["CITIES_LIST", countryName, stateName],
    enabled: !!countryName,
    queryFn: async () => {
      const params: Record<string, string> = {
        country_name: countryName,
      };

      if (stateName) {
        params.state_name = stateName;
      }

      const response = await apiClient.get<CitiesListResponse>(
        Routes.CITIES_LIST,
        {
          params,
        }
      );

      const cities = response?.data?.data?.cities || [];

      return cities.map((city: string) => ({
        id: city,
        label: city,
      }));
    },
  });
};
/**
 * Fetches list of business type options
 * @param organizationId - Optional organization ID
 * @returns Promise with array of business type SelectOptions
 */
export const useBusinessTypes = (organizationId?: string) => {
  return useQuery({
    queryKey: ["BUSINESS_TYPES", organizationId],
    queryFn: async () => {
      const params: Record<string, string> = {};

      if (organizationId) {
        params.organization_id = organizationId;
      }

      const response = await apiClient.get<BusinessTypesResponse>(
        Routes.BUSINESS_TYPE_OPTIONS,
        {
          params,
        }
      );

      const businessTypes = response?.data?.data?.business_types || [];

      return businessTypes.map((bt: BusinessTypeOption) => ({
        id: bt.name,
        label: bt.name,
      }));
    },
  });
};

export const useGetCountryFromIp = () => {
  return useQuery({
    queryKey: ["GET_COUNTRY_FROM_IP"],
    queryFn: () => apiClient.get<CountryFromIpResponse>(Routes.COUNTRY_FROM_IP),
  });
};
